import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useReducedMotion } from "../lib/useReducedMotion";

const GAP = 14;
const EDGE = 12;

export interface CoachmarkProps {
  /** The element being pointed at. Nothing renders until it exists. */
  anchor: HTMLElement | null;
  title: string;
  body: string;
  step: number;
  total: number;
  nextLabel: string;
  onNext: () => void;
  onDismiss: () => void;
  dismissLabel?: string;
}

/**
 * A card pointed at something already on screen.
 *
 * Positioned by hand rather than by a tour library: the anchors are rows in a
 * fixed-width sticky rail with room to their right, so there is no collision
 * or flipping to solve, and the libraries that would solve it bring their own
 * DOM, their own styling to override, and their own accessibility gaps.
 *
 * Non-modal on purpose. It explains a place the member can still reach, so it
 * does not trap focus; it takes focus on open, restores it on close, and Esc
 * always ends it.
 */
export function Coachmark({
  anchor,
  title,
  body,
  step,
  total,
  nextLabel,
  onNext,
  onDismiss,
  dismissLabel = "Skip",
}: CoachmarkProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const reducedMotion = useReducedMotion();
  const titleId = useId();
  const bodyId = useId();
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Layout effect so the card never paints at 0,0 before being placed.
  useLayoutEffect(() => {
    if (!anchor) return;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const card = cardRef.current?.getBoundingClientRect();
      const height = card?.height ?? 0;
      const top = Math.min(
        Math.max(EDGE, a.top + a.height / 2 - height / 2),
        Math.max(EDGE, window.innerHeight - height - EDGE),
      );
      setPos({ top, left: a.right + GAP });
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(anchor);
    if (cardRef.current) observer.observe(cardRef.current);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor]);

  // Draws the eye to the thing being described, the same gentle pulse the
  // attention card uses when it wants to be looked at.
  useEffect(() => {
    if (!anchor) return;
    anchor.setAttribute("data-coachmark-target", "true");
    return () => {
      anchor.removeAttribute("data-coachmark-target");
    };
  }, [anchor]);

  useEffect(() => {
    if (!anchor) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    cardRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onDismiss();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      restoreRef.current?.focus?.();
    };
  }, [anchor, onDismiss]);

  if (!anchor) return null;

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      tabIndex={-1}
      className="coachmark"
      data-reduced-motion={reducedMotion ? "true" : undefined}
      style={pos ? { top: pos.top, left: pos.left } : { opacity: 0 }}
    >
      <span aria-hidden="true" className="coachmark__arrow" />
      <p className="coachmark__step">
        {step} of {total}
      </p>
      <h2 id={titleId} className="coachmark__title">
        {title}
      </h2>
      <p id={bodyId} className="coachmark__body">
        {body}
      </p>
      <div className="coachmark__actions">
        <button type="button" className="coachmark__next" onClick={onNext}>
          {nextLabel}
        </button>
        <button type="button" className="coachmark__dismiss" onClick={onDismiss}>
          {dismissLabel}
        </button>
      </div>
    </div>
  );
}
